import React, { useState } from 'react';
import { ShieldCheck, Clock, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/leads`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', service_type: '' });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen pt-12 hero-pattern"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-[#E0F2FE] text-[#0EA5E9] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              <span>24/7 Emergency Service Available</span>
            </div>

            <h1
              data-testid="hero-headline"
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#0F172A] leading-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Perfect Climate,
              <br />
              <span className="text-[#F97316]">Guaranteed.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-lg"
            >
              Connect with trusted, licensed HVAC professionals in your area. 
              Get free quotes for AC repairs, heating installations, and more.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#0EA5E9]" />
                <span className="text-sm text-slate-600 font-medium">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#F97316]" />
                <span className="text-sm text-slate-600 font-medium">4.9 Star Rating</span>
              </div>
            </div>

            {/* Mobile Form Trigger */}
            <div className="lg:hidden">
              <Button
                data-testid="mobile-quote-btn"
                onClick={() => document.getElementById('lead-form').scrollIntoView({ behavior: 'smooth' })}
                className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg orange-glow"
              >
                Get Your Free Quote
              </Button>
            </div>
          </div>

          {/* Right Content - Form */}
          <div className="animate-slide-up stagger-2">
            <div
              id="lead-form"
              data-testid="lead-form-container"
              className="bg-white p-8 md:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100"
            >
              <h2
                className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Get Free Quotes
              </h2>
              <p className="text-slate-500 mb-6">
                Fill out the form and a local HVAC pro will contact you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="form-name-input"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1.5 h-12 border-slate-200 focus:border-[#0EA5E9] focus:ring-[#0EA5E9] rounded-lg"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    data-testid="form-email-input"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1.5 h-12 border-slate-200 focus:border-[#0EA5E9] focus:ring-[#0EA5E9] rounded-lg"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-700 font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="form-phone-input"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1.5 h-12 border-slate-200 focus:border-[#0EA5E9] focus:ring-[#0EA5E9] rounded-lg"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-slate-700 font-medium">
                    Service Needed *
                  </Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={handleServiceChange}
                  >
                    <SelectTrigger
                      data-testid="form-service-select"
                      className="mt-1.5 h-12 border-slate-200 focus:border-[#0EA5E9] focus:ring-[#0EA5E9] rounded-lg"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ac-repair" data-testid="service-ac-repair">
                        AC Repair & Service
                      </SelectItem>
                      <SelectItem value="ac-installation" data-testid="service-ac-installation">
                        AC Installation
                      </SelectItem>
                      <SelectItem value="heating-repair" data-testid="service-heating-repair">
                        Heating Repair
                      </SelectItem>
                      <SelectItem value="heating-installation" data-testid="service-heating-installation">
                        Heating Installation
                      </SelectItem>
                      <SelectItem value="duct-cleaning" data-testid="service-duct-cleaning">
                        Duct Cleaning
                      </SelectItem>
                      <SelectItem value="maintenance" data-testid="service-maintenance">
                        Maintenance Plan
                      </SelectItem>
                      <SelectItem value="emergency" data-testid="service-emergency">
                        Emergency Service
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  data-testid="form-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg orange-glow transition-all duration-300 transform hover:-translate-y-1"
                >
                  {isSubmitting ? 'Submitting...' : 'Get My Instant Estimate'}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  No spam. We respect your privacy.
                </p>

                <div className="text-center pt-2 border-t border-slate-100">
                  <a 
                    href="/instant-hvac-estimate-new-orleans" 
                    className="text-sm text-[#0EA5E9] hover:underline"
                  >
                    Or use our Instant Pricing Tool →
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-40 right-0 w-96 h-96 bg-[#0EA5E9]/5 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-[#F97316]/5 rounded-full blur-3xl -z-10" />
    </section>
  );
};

export default HeroSection;
